import { PracticeOffer } from "../soulumoPractice/PracticeOffer";
import React, { useRef, useState } from "react";
import { NightStartButton, NightStartOverlay } from "./NightStart";
import axios from "axios";
import { Headphones, Loader2 } from "lucide-react";
import { queueLabel } from "../../lib/queueLabel";
import { API, auth, TOK, SERIF, SANS, PrimaryBtn, QuietLink, Title, startCloneCheckout } from "./shared";
import { MusicChangeButton, SaveAudioButton } from "./InstallationActions";

export const TrialKeep = ({ journey, onKept }) => {
  const [busy, setBusy] = useState("");
  const keep = async (mode) => {
    setBusy(mode);
    try {
      const r = await axios.post(`${API}/api/audio-journeys/${journey.id}/keep`, { mode }, auth());
      if (r.data?.needs_subscription) return startCloneCheckout();
      onKept(mode);
    } finally { setBusy(""); }
  };
  return (
    <div className="space-y-3 pt-2" data-testid="simple-trial-keep">
      <p className="text-center text-lg italic" style={{ color: TOK.creamDim, fontFamily: SERIF }}>Would you like to keep it?</p>
      <PrimaryBtn testId="simple-keep-my-voice" disabled={!!busy} onClick={() => keep("clone")}>
        {busy === "clone" ? <Loader2 size={16} className="animate-spin" /> : null} Keep it in my voice · <s className="mr-1 opacity-60" data-testid="ready-was-price">£9.99</s>£6.99/mo
      </PrimaryBtn>
      <QuietLink testId="simple-keep-narrator" onClick={() => keep("narrator")}>
        {busy === "narrator" ? "Re-weaving…" : "Keep it free in the Soulumo voice"}
      </QuietLink>
    </div>
  );
};

export const CreatingStep = ({ journey }) => (
  <div className="py-14 flex flex-col items-center gap-5 text-center" data-testid="simple-creating">
    <div className="w-20 h-20 rounded-full animate-pulse" style={{ background: "radial-gradient(circle, rgba(215,187,122,0.65), transparent 70%)" }} />
    <Title>Soulumo is building everything</Title>
    <p className="text-sm" style={{ color: TOK.creamMist, fontFamily: SANS }} data-testid="simple-creating-queue">
      {journey ? queueLabel(journey) : "Weaving your words, voice and journey…"}
    </p>
  </div>
);

export const ReadyStep = ({ journey, onDone, onRemusic }) => {
  const [kept, setKept] = useState(!journey.clone_trial_pending);
  const ready = journey.audio_status === "ready";
  const player = useRef(null);
  const [night, setNight] = useState(false);
  const startNight = () => {
    const a = player.current;
    a.play().then(() => { a.pause(); a.currentTime = 0; }).catch(() => {});
    setNight(true);
  };
  return (
    <div className="space-y-7 text-center" data-testid="simple-ready-step">
      <Headphones size={34} className="mx-auto" style={{ color: TOK.gold }} />
      <Title>{ready ? "Your installation is ready" : "Almost there"}</Title>
      {ready ? (
        <>
          <audio ref={player} controls autoPlay src={`${API}/api/audio-journeys/${journey.id}/audio`} className="w-full" data-testid="simple-ready-player" />
          <NightStartButton onClick={startNight} />
          {night && <NightStartOverlay onCancel={() => setNight(false)} onFinish={() => { setNight(false); player.current?.play().catch(() => {}); }} />}
          {kept && <PracticeOffer journeyId={journey.id} />}
        </>
      ) : (
        <p className="text-lg italic" style={{ color: TOK.creamDim, fontFamily: SERIF }} data-testid="simple-ready-later">
          {queueLabel(journey)}. It will be waiting in your library — we'll email you the moment it's ready.
        </p>
      )}
      {kept && ready && (
        <div className="space-y-2" data-testid="simple-ready-extras">
          <p className="text-sm italic" style={{ color: TOK.creamMist, fontFamily: SERIF }}>Want a different feel? Keep the same words and try other music. It's free, up to 3 times.</p>
          <div className="flex justify-center gap-2 flex-wrap">
            <MusicChangeButton j={journey} onDone={onRemusic} />
            <SaveAudioButton j={journey} />
          </div>
        </div>
      )}
      {!kept && ready ? <TrialKeep journey={journey} onKept={() => setKept(true)} /> : (
        <PrimaryBtn testId="simple-ready-done" onClick={onDone}>Go to my library</PrimaryBtn>
      )}
      {!ready && journey.clone_trial_pending && (
        <p className="text-xs" style={{ color: TOK.creamMist, fontFamily: SANS }}>When it's ready, listen first — then decide whether to keep it.</p>
      )}
    </div>
  );
};
